import React from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { formatCurrency, formatCurrencyCompact, formatPercent } from "../../utils/formatters";
import { DEMO_SAVINGS_RATE } from "./demoData";

function SavingsTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-md text-sm">
            <p className="text-gray-500 mb-1">{d.label}</p>
            <p className="text-gray-900">Income: {formatCurrency(d.income)}</p>
            <p className="text-gray-900">Expenses: {formatCurrency(d.expenses)}</p>
            <p className={`font-medium ${d.netSavings >= 0 ? "text-accent-600" : "text-danger-500"}`}>
                Saved: {formatCurrency(d.netSavings)}
            </p>
            <p className={`font-medium ${d.savingsRate >= 0 ? "text-accent-600" : "text-danger-500"}`}>
                Rate: {formatPercent(d.savingsRate)}
            </p>
        </div>
    );
}

export default function DemoSavingsRateCard({ months = 6, onMonthsChange }) {
    const allMonths = DEMO_SAVINGS_RATE.months;
    const filtered = allMonths.slice(-months);

    const totalIncome = filtered.reduce((s, m) => s + m.income, 0);
    const totalExpenses = filtered.reduce((s, m) => s + m.expenses, 0);
    const totalSavings = totalIncome - totalExpenses;
    const avgIncome = totalIncome / filtered.length;
    const avgExpenses = totalExpenses / filtered.length;
    const avgSavings = totalSavings / filtered.length;
    const rate = totalIncome > 0 ? (totalSavings / totalIncome) * 100 : 0;

    const chartData = filtered.map(m => {
        const date = new Date(m.month + "T00:00:00");
        return {
            label: date.toLocaleDateString("en-US", { month: "short", year: "2-digit" }),
            income: m.income,
            expenses: m.expenses,
            netSavings: m.netSavings,
            savingsRate: m.savingsRate,
        };
    });

    const rateColor = rate >= 50 ? "text-accent-600" : rate >= 20 ? "text-amber-600" : "text-danger-500";

    return (
        <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-gray-900">Savings Rate</h3>
                        {onMonthsChange && (
                            <div className="flex gap-1 text-xs">
                                {[1, 3, 6].map(m => (
                                    <button
                                        key={m}
                                        onClick={() => onMonthsChange(m)}
                                        className={`px-2 py-0.5 rounded ${
                                            months === m
                                                ? "bg-accent-100 text-accent-600"
                                                : "text-gray-500 hover:text-gray-900"
                                        }`}
                                    >
                                        {m}mo
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <span className="text-xs text-gray-500">
                        Review transactions
                    </span>
                </div>
                <span className={`text-2xl font-bold ${rateColor}`}>
                    {formatPercent(rate)}
                </span>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
                <div>
                    <p className="text-gray-500">Avg Income</p>
                    <p className="text-gray-900 font-medium">{formatCurrencyCompact(avgIncome)}</p>
                </div>
                <div>
                    <p className="text-gray-500">Avg Expenses</p>
                    <p className="text-gray-900 font-medium">{formatCurrencyCompact(avgExpenses)}</p>
                </div>
                <div>
                    <p className="text-gray-500">Avg Saved</p>
                    <p className={`font-medium ${avgSavings >= 0 ? "text-accent-600" : "text-danger-500"}`}>
                        {formatCurrencyCompact(avgSavings)}
                    </p>
                </div>
            </div>

            {chartData.length > 1 && (
                <div className="h-40" style={{ willChange: "transform" }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} barGap={2}>
                            <XAxis
                                dataKey="label"
                                tick={{ fill: "#94A3B8", fontSize: 12 }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis
                                tick={{ fill: "#94A3B8", fontSize: 12 }}
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={(v) => formatCurrencyCompact(v)}
                                width={60}
                            />
                            <Tooltip content={<SavingsTooltip />} />
                            <ReferenceLine y={0} stroke="#E2E8F0" />
                            <Bar
                                dataKey="netSavings"
                                radius={[4, 4, 0, 0]}
                                fill="#2563EB"
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}
